// What a product page says when a shopper writes a review, in each language a store is served in: the button, the
// form's fields, what is missing, and the thanks that tells them the shop approves a review before it shows.

/** Every phrase the review writer is drawn with. */
export type ReviewWriteWords = {
  /** The language these words are in, which the stars are read out in too. */
  language: 'en' | 'fr' | 'ar';
  write: string;
  none: string;
  title: string;
  close: string;
  rating: string;
  name: string;
  phone: string;
  phoneHint: string;
  review: string;
  reviewHint: string;
  photos: string;
  send: string;
  sending: string;
  thanks: string;
  thanksHint: string;
  failed: string;
  tooMany: string;
  needRating: string;
  needName: string;
  needReview: string;
  /** The heading over the store's reply, and the names of a review's voice note and video. */
  reply: string;
  voiceNote: string;
  video: string;
};

/** The words in English, French and Arabic. */
export const REVIEW_WRITE_WORDS: Readonly<Record<'en' | 'fr' | 'ar', ReviewWriteWords>> = {
  en: {
    language: 'en', write: 'Write a review', none: 'No reviews yet. Be the first to share what you think.', title: 'Write a review', close: 'Close',
    rating: 'Your rating', name: 'Your name', phone: 'Phone (optional)', phoneHint: 'The number you ordered with. It is never shown.',
    review: 'Your review', reviewHint: 'What did you like? How is the fit, the quality?', photos: 'Add photos',
    send: 'Send my review', sending: 'Sending…', thanks: 'Thank you for your review!',
    thanksHint: 'It will appear here once the shop has read it.', failed: 'We could not send your review just now. Please try again.',
    tooMany: 'Too many reviews sent from here. Please wait a minute, then try again.',
    needRating: 'Choose from 1 to 5 stars.', needName: 'Enter your name.', needReview: 'Write a few words about the product.',
    reply: 'Reply from the shop', voiceNote: 'Voice note from the reviewer', video: 'Video from the reviewer'
  },
  fr: {
    language: 'fr', write: 'Écrire un avis', none: 'Pas encore d’avis. Soyez le premier à donner le vôtre.', title: 'Écrire un avis', close: 'Fermer',
    rating: 'Votre note', name: 'Votre nom', phone: 'Téléphone (facultatif)', phoneHint: 'Le numéro de votre commande. Il n’est jamais affiché.',
    review: 'Votre avis', reviewHint: 'Qu’avez-vous aimé ? La taille, la qualité ?', photos: 'Ajouter des photos',
    send: 'Envoyer mon avis', sending: 'Envoi…', thanks: 'Merci pour votre avis !',
    thanksHint: 'Il apparaîtra ici dès que la boutique l’aura lu.', failed: 'Impossible d’envoyer votre avis pour le moment. Veuillez réessayer.',
    tooMany: 'Trop d’avis envoyés d’ici. Patientez une minute, puis réessayez.',
    needRating: 'Choisissez de 1 à 5 étoiles.', needName: 'Indiquez votre nom.', needReview: 'Écrivez quelques mots sur le produit.',
    reply: 'Réponse de la boutique', voiceNote: 'Message vocal du client', video: 'Vidéo du client'
  },
  ar: {
    language: 'ar', write: 'اكتب تقييماً', none: 'لا توجد تقييمات بعد. كن أول من يشارك رأيه.', title: 'اكتب تقييماً', close: 'إغلاق',
    rating: 'تقييمك', name: 'اسمك', phone: 'الهاتف (اختياري)', phoneHint: 'الرقم الذي طلبت به. لا يظهر أبداً.',
    review: 'رأيك', reviewHint: 'ما الذي أعجبك؟ المقاس، الجودة؟', photos: 'أضف صوراً',
    send: 'أرسل تقييمي', sending: 'جارٍ الإرسال…', thanks: 'شكراً على تقييمك!',
    thanksHint: 'سيظهر هنا بعد أن يطّلع عليه المتجر.', failed: 'تعذّر إرسال تقييمك الآن. حاول مرة أخرى.',
    tooMany: 'تم إرسال تقييمات كثيرة من هنا. انتظر دقيقة ثم حاول مجدداً.',
    needRating: 'اختر من 1 إلى 5 نجوم.', needName: 'أدخل اسمك.', needReview: 'اكتب بضع كلمات عن المنتج.',
    reply: 'رد المتجر', voiceNote: 'رسالة صوتية من الزبون', video: 'فيديو من الزبون'
  }
};
